import {
  axiosDelete,
  axiosGet,
  axiosPatch,
  axiosPost,
  axiosPut,
} from '@/api/handlers';
import { useMutation, useQuery } from '@/api/hooks';
import { useInvalidateQuery, useRemoveQueries } from '@/hooks';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import {
  ActivitiesModel,
  ActivityFlowModel,
  CreateActivityParamsSchema,
  IndicatorFormSchema,
  IndicatorModel,
  IndicatorsModel,
  UpdateActivityParamsSchema,
  UpdateConsecutiveIndicatorRequestSchema,
} from '../projects/_models/activity';
import { INDICATOR_LABEL_SEPARATOR } from '../review-sheet/utils/activity';
import { queryKeyMap } from '../review-sheet/utils/query-keys';
import {
  type PathSegment,
  transformFlowData,
} from '../review-sheet/utils/transform-flow-data';

export const useActivities = (params: { projectId: string }) => {
  return useQuery({
    queryKey: queryKeyMap.activities(params.projectId),
    queryFn: async () => {
      const res = await axiosGet(
        `/api/projects/${params.projectId}/activities/`,
      );
      return ActivitiesModel.parse(res);
    },
  });
};

export const useCreateActivity = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: z.infer<typeof CreateActivityParamsSchema>) => {
      const { projectId, ...parsed } = CreateActivityParamsSchema.parse(values);
      const res = await axiosPost(
        `/api/projects/${projectId}/activities/`,
        parsed,
      );
      await Promise.all([
        invalidate(queryKeyMap.activities(projectId)),
        invalidate(queryKeyMap.activityTree(projectId)),
      ]);
      return res;
    },
  });
};

export const useUpdateActivity = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: z.infer<typeof UpdateActivityParamsSchema>) => {
      const { projectId, id, ...parsed } =
        UpdateActivityParamsSchema.parse(values);
      const res = await axiosPatch(
        `/api/projects/${projectId}/activities/${id}/`,
        parsed,
      );
      await Promise.all([
        invalidate(queryKeyMap.activities(projectId)),
        invalidate(queryKeyMap.activityTree(projectId)),
      ]);
      return res;
    },
  });
};

const _useDeleteActivity = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (values: { projectId: string; id: string }) => {
      const res = await axiosDelete(
        `/api/projects/${values.projectId}/activities/${values.id}/`,
      );
      await Promise.all([
        invalidate(queryKeyMap.activities(values.projectId)),
        invalidate(queryKeyMap.activityTree(values.projectId)),
      ]);
      return res;
    },
  });
};

const useActivityTree = <T = z.infer<typeof ActivityFlowModel>>(
  id: string,
  select?: (data: z.infer<typeof ActivityFlowModel>) => T,
) => {
  return useQuery({
    queryKey: queryKeyMap.activityTree(id),
    queryFn: async () => {
      const res = await axiosGet(`/api/projects/${id}/activity-flows/`);
      return ActivityFlowModel.parse(res);
    },
    select,
  });
};

export const useActivityTreeEdges = (id: string) => {
  const { t } = useTranslation();
  return useActivityTree(id, (data) => {
    return data.edges.map((edge) => {
      const targetNode = data.nodes.find(
        (node) => node.id === edge.targetNodeId,
      );
      const sourceNode = data.nodes.find(
        (node) => node.id === edge.sourceNodeId,
      );

      invariant(targetNode && sourceNode, t('unreachable-code'));

      return {
        ...edge,
        targetNode,
        sourceNode,
      };
    });
  });
};

export const useActivityTreeOptions = (id: string) => {
  const tree = useActivityTree(id, (res) => {
    return {
      nodes: res.nodes.map((node, index) => ({
        ...node,
        routeNumber: index + 1,
      })),
      edges: res.edges,
    };
  });

  const routesInfo = transformFlowData({
    nodes: tree.data?.nodes ?? [],
    edges: tree.data?.edges ?? [],
  });

  const selectOptions = routesInfo
    .flatMap((route) => {
      const originNode = tree.data?.nodes.find(
        (node) => node.id === route.originNodeId,
      );
      const rootActivity = originNode?.activity;

      if (!originNode || !rootActivity) {
        return [];
      }

      return route.routes.flatMap((subRoute) => {
        const routeIndicators = subRoute.paths.flatMap(
          (
            path,
          ): {
            indicator: z.infer<typeof IndicatorModel>;
            path: PathSegment;
            routeNumber: number;
          }[] => {
            const node = tree.data?.nodes.find(
              (node) => node.id === path.nodeId,
            );
            const indicator = node?.indicator;

            if (!indicator) {
              return [];
            }

            return [{ indicator, path, routeNumber: node.routeNumber }];
          },
        );

        return {
          label: `${rootActivity.sortOrder}-${routeIndicators
            .map((route) => route.indicator.sortOrder)
            .join(INDICATOR_LABEL_SEPARATOR)}`,
          value: crypto.randomUUID(),
          rootActivity,
          routeIndicators,
        };
      });
    })
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { numeric: true }),
    );

  return {
    tree,
    selectOptions,
  };
};

export const useIndicators = <T = z.infer<typeof IndicatorModel>, U = T[]>({
  filters,
  select,
  enabled = true,
}: {
  filters?: {
    fiscalYear?: string;
    keyId?: string;
    ordering?: string;
  };
  select?: (data: z.infer<typeof IndicatorsModel>) => U;
  enabled?: boolean;
}) => {
  const params = {
    ...filters,
    key: 'project',
  };
  return useQuery({
    queryKey: queryKeyMap.indicators(params),
    queryFn: async () => {
      const indicators = await axiosGet('/api/indicators/', params);
      return IndicatorsModel.parse(indicators);
    },
    select,
    enabled,
  });
};

export const useIndicator = ({
  id,
  enabled = true,
}: {
  id: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: queryKeyMap.indicator(id),
    queryFn: async () => {
      const indicators = await axiosGet(`/api/indicators/${id}/`);
      return IndicatorModel.parse(indicators);
    },
    enabled: !!id && enabled,
  });
};

export const useCreateIndicator = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof IndicatorFormSchema> & {
        json: {
          id: string;
        };
      },
    ) => {
      const { json, ...restValues } = values;
      const { ...payload } = IndicatorFormSchema.parse(restValues);
      const res = await axiosPost('/api/indicators/', {
        key: 'project',
        json,
        ...payload,
        title: 'dummy',
        fiscalYear: '2025',
      });

      await invalidate(queryKeyMap.indicators());

      return res;
    },
  });
};

export const useUpdateIndicator = () => {
  const { invalidate } = useInvalidateQuery();
  return useMutation({
    mutationFn: async (
      values: z.infer<typeof IndicatorFormSchema> & {
        id: string;
        key?: string | null;
        json?: any;
      },
    ) => {
      const { id, key, json, ...restValues } = values;
      const { ...payload } = IndicatorFormSchema.parse(restValues);
      const res = await axiosPatch(`/api/indicators/${id}/`, {
        ...(key !== undefined && { key }),
        ...(json !== undefined && { json }),
        ...payload,
        title: 'dummy',
        fiscalYear: '2025',
      });

      await Promise.all([
        invalidate(queryKeyMap.indicators()),
        invalidate(queryKeyMap.indicator(id)),
        invalidate(queryKeyMap.consecutiveIndicators(id)),
        invalidate(queryKeyMap.allConsecutiveIndicators()),
        invalidate(queryKeyMap.activityTree()),
      ]);

      return res;
    },
  });
};

export const useDeleteIndicator = () => {
  const { invalidate } = useInvalidateQuery();
  const { removeQuery } = useRemoveQueries();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axiosDelete(`/api/indicators/${id}/`);

      await invalidate(queryKeyMap.indicators());

      removeQuery(queryKeyMap.indicator(id));
      removeQuery(queryKeyMap.consecutiveIndicators(id));
      removeQuery(queryKeyMap.allConsecutiveIndicators());

      return res;
    },
  });
};

const fetchConsecutiveIndicators = async (
  id: string,
): Promise<z.infer<typeof IndicatorsModel>> => {
  const res = await axiosGet(`/api/indicators/${id}/consecutive/`);
  return IndicatorsModel.parse(res);
};

export const useConsecutiveIndicators = (
  id: string | undefined = '',
  options: {
    enabled?: boolean;
  } = {
    enabled: true,
  },
) => {
  return useQuery({
    queryKey: queryKeyMap.consecutiveIndicators(id),
    queryFn: async () => {
      return await fetchConsecutiveIndicators(id);
    },
    enabled: options.enabled,
  });
};

export const useAllConsecutiveIndicators = (ids: string[]) => {
  return useQuery({
    queryKey: queryKeyMap.allConsecutiveIndicators(),
    queryFn: async () => {
      const result = await Promise.all(
        ids.map(async (id) => {
          const res = await fetchConsecutiveIndicators(id);
          return { [id]: res };
        }),
      );
      const res = result.reduce<
        Record<string, z.infer<typeof IndicatorsModel>>
      >((acc, item) => {
        const key = Object.keys(item)[0];
        if (key && item[key] !== undefined) {
          acc[key] = item[key];
        }
        return acc;
      }, {});
      return res;
    },
    enabled: !!ids.length,
  });
};

export const useNestedConsecutiveIndicators = (ids: string[]) => {
  return useQuery({
    queryKey: queryKeyMap.nestedConsecutiveIndicators(ids),
    queryFn: async () =>
      await Promise.all(
        ids.map(async (id) => {
          const res = await axiosGet(`/api/indicators/${id}/consecutive/`);
          return IndicatorsModel.parse(res);
        }),
      ),
    enabled: !!ids.length,
  });
};

export const useUpdateConsecutiveIndicator = () => {
  const { invalidate } = useInvalidateQuery();

  return useMutation({
    mutationFn: async (
      values: z.infer<typeof UpdateConsecutiveIndicatorRequestSchema> & {
        targetYears: number[];
      },
    ) => {
      const { targetYears, ...rest } = values;
      const parsed = UpdateConsecutiveIndicatorRequestSchema.parse(rest);
      const payload: z.infer<typeof UpdateConsecutiveIndicatorRequestSchema> = {
        ...parsed,
        annualValues: parsed.annualValues.map(
          ({ isManualAchievementRate, achievementRate, ...annualValue }) => ({
            ...annualValue,
            achievementRate: isManualAchievementRate ? achievementRate : null,
            isTargetYear: targetYears.includes(annualValue.year),
          }),
        ),
      };

      const res = await axiosPut('/api/indicators/consecutive/', payload);

      await Promise.all([
        invalidate(queryKeyMap.consecutiveIndicators(values.baseIndicatorId)),
        invalidate(queryKeyMap.allConsecutiveIndicators()),
        invalidate(queryKeyMap.indicators()),
        invalidate(queryKeyMap.indicator(values.baseIndicatorId), 'all'),
      ]);

      return IndicatorsModel.parse(res);
    },
  });
};
